import { useEffect, useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Clock3, ShieldCheck, Award, Lock } from "lucide-react";
import { getService } from "@/api";
import { useSeo, faqSchema } from "@/seo";
import { Reveal, SectionHead, Eyebrow } from "@/components/Reveal";
import { CalendlyCta } from "@/components/SiteChrome";

const TEAM_IMG = "https://images.unsplash.com/photo-1577962917302-cd874c4e31d2?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";

const WHO = [
  { segment: "UAE & GCC audit firms", need: "Audit file preparation, working papers, IFRS statements, peak-season capacity" },
  { segment: "Accounting & tax firms, FTA tax agents", need: "Bookkeeping, VAT and Corporate Tax returns, transfer pricing files, e-invoicing data" },
  { segment: "Consulting & corporate service providers", need: "Back office for their SME clients, MIS, FP&A, payroll processing" },
  { segment: "Group companies & family offices", need: "A shared-service finance team across multiple UAE and GCC entities" },
];

const SERVICES = [
  { title: "Accounting & Bookkeeping", body: "Ledgers, AP/AR, reconciliations, month-end close, fixed assets and IFRS financial statement drafts, on the client's own software." },
  { title: "Audit Support", body: "Lead schedules, trial-balance mapping, substantive testing, confirmation tracking and disclosure checklists, prepared to your methodology." },
  { title: "UAE Corporate Tax", body: "Taxable income computations, QFZP tests, exempt-income workings and draft returns ready for filing on EmaraTax." },
  { title: "Transfer Pricing", body: "Disclosure Form schedules, Master and Local Files, benchmarking studies and intercompany agreement drafts." },
  { title: "VAT & E-Invoicing", body: "Returns, ledger-to-return reconciliations, refund claims and PINT AE data readiness ahead of the 2027 e-invoicing phases." },
  { title: "GCC Tax", body: "KSA VAT, Zakat and ZATCA Fatoora support, plus Oman, Bahrain, Qatar and Kuwait compliance workings." },
  { title: "Payroll & WPS", body: "Monthly payroll computation, WPS salary files, payslips and end-of-service gratuity schedules." },
  { title: "CFO & Practice Support", body: "Budgets, forecasts, board packs, valuation workings, research digests and proposal drafting behind your advisory work." },
];

const MODELS = [
  { title: "Dedicated team (FTE)", body: "Named staff work only for your firm, on your systems and hours.", best: "Steady, year-round volume" },
  { title: "Shared pool", body: "Capacity drawn from a trained pool as needed.", best: "Smaller firms, variable volume" },
  { title: "Per deliverable", body: "Fixed fee per return, file or monthly close.", best: "VAT/CT returns, bookkeeping" },
  { title: "Peak-season surge", body: "Extra associates for the January April audits and the September CT deadline.", best: "Audit firms, tax agents" },
  { title: "Build-Operate-Transfer", body: "We set up and run an India team, then transfer it to your own Indian entity.", best: "Firms building their own centre" },
];

const JOURNEY = [
  { step: "Scope call", body: "30 minutes. We map the services, volumes and software you use." },
  { step: "Paid pilot", body: "Two weeks. A dedicated team works on live files under your review." },
  { step: "Onboard", body: "NDA, data-protection agreement, access set-up on your systems, SOPs signed off." },
  { step: "Scale", body: "Add people or service lines as the season demands." },
];

const WHY = [
  { icon: Award, title: "UAE-specific, not generic", body: "Teams trained on Federal Decree-Law No. 47 of 2022, the VAT amendments and the e-invoicing guidelines." },
  { icon: Clock3, title: "Same-day turnaround", body: "India is 1.5 hours ahead, work sent at the end of the Dubai day is back by your next morning." },
  { icon: ShieldCheck, title: "Your brand, your client", body: "We work white-label by default and never contact your clients unless you ask us to." },
  { icon: Lock, title: "Reviewed twice, secure by design", body: "Every deliverable is prepared and reviewed in India, on your systems through VPN/VDI, before it reaches you." },
];

const FAQS = [
  { q: "Is outsourcing to India allowed for UAE audit and tax work?", a: "Yes, for preparation work. The UAE firm remains responsible for the audit opinion, the tax agent's filing and all client advice, we prepare under its supervision." },
  { q: "Will our clients know you exist?", a: "Only if you want them to. We work white-label by default and sign a non-solicitation clause in every agreement." },
  { q: "Which software do you use?", a: "Yours. Our teams work on Zoho Books, Xero, QuickBooks, Tally, Odoo, SAP Business One, NetSuite, CaseWare and Excel-based templates." },
  { q: "How fast can a team start?", a: "A pilot can start within days of signing the NDA; a dedicated team typically within a few weeks." },
  { q: "Can our client data legally leave the UAE?", a: "In most cases yes, with the right safeguards. We sign cross-border transfer clauses and work only on your systems; regulated sectors such as banking have stricter rules we work within." },
];

export default function ServiceGcc() {
  const [service, setService] = useState(null);
  useSeo({
    title: "India Back Office for UAE & GCC Firms | White-Label, TruVerse",
    description:
      "Trained Indian CAs for UAE Corporate Tax, VAT, audit support and bookkeeping. White-label delivery, 1.5 hours from Dubai. Your brand, your client.",
    schema: faqSchema(FAQS),
  });
  useEffect(() => {
    getService("gcc-india-back-office").then(setService).catch(() => setService({}));
  }, []);

  if (!service) return <div className="container-site py-32 text-muted-foreground" data-testid="service-gcc-loading">Loading…</div>;

  return (
    <div data-testid="service-gcc-page">
      <section className="relative overflow-hidden bg-ink py-24 lg:py-32">
        <img src={TEAM_IMG} alt="Chartered accountants and finance professionals in India" className="absolute inset-0 h-full w-full object-cover opacity-15" loading="eager" />
        <div className="absolute inset-0 bg-[hsl(231_47%8%/0.65)]" aria-hidden="true" />
        <div className="container-site relative max-w-4xl">
          <Reveal>
            <h1 className="mt-2 text-4xl font-bold leading-tight tracking-tight text-ink-fg md:text-6xl">
              Your UAE practice. Our India bench.
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink-muted md:text-lg">
              Chartered Accountants and finance professionals in India, trained on UAE Corporate Tax, VAT, e-invoicing
              and IFRS, working under your brand, your methodology and your review.
            </p>
            <div className="mt-10">
              <CalendlyCta label="Book a 2-week pilot" testId="gcc-cta-book-btn" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-24" data-testid="gcc-who-section">
        <div className="container-site">
          <Reveal>
            <SectionHead eyebrow="Who we serve" title="A collaboration, not a hand-off." intro="We work behind UAE and wider GCC firms as a white-label partner. Your firm owns the client, signs the opinion or the tax return and stays licensed; we prepare, process and document from India under your review." />
          </Reveal>
          <div className="mt-14 grid gap-px border border-border bg-border md:grid-cols-2">
            {WHO.map((w, i) => (
              <Reveal key={w.segment} delay={i * 0.06} className="bg-card">
                <div className="h-full p-8" data-testid={`gcc-who-${i}`}>
                  <h3 className="text-lg font-bold text-foreground">{w.segment}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{w.need}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-secondary/60 py-24" data-testid="gcc-services-section">
        <div className="container-site">
          <Reveal>
            <SectionHead eyebrow="Practice areas" title="Eight practice areas, one collaboration." intro="We deliver under your brand, your methodology and your templates. Anything that needs a UAE licence, a signature, a site visit or a regulator login stays with your firm, and that split is what keeps the model compliant." />
          </Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="mt-14 w-full" data-testid="gcc-services-accordion">
              {SERVICES.map((s, i) => (
                <AccordionItem key={s.title} value={`item-${i}`}>
                  <AccordionTrigger className="text-left text-lg font-semibold text-foreground" data-testid={`gcc-service-trigger-${i}`}>
                    {s.title}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-muted-foreground">{s.body}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <section className="py-24" data-testid="gcc-how-section">
        <div className="container-site">
          <Reveal>
            <SectionHead eyebrow="How it works" title="From scope call to scale." />
          </Reveal>
          <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {JOURNEY.map((j, i) => (
              <Reveal key={j.step} delay={i * 0.08}>
                <div className="border-t-2 border-accent pt-8" data-testid={`gcc-journey-step-${i + 1}`}>
                  <span className="text-4xl font-bold text-accent/30">{i + 1}</span>
                  <h3 className="mt-3 text-xl font-bold text-foreground">{j.step}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{j.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-secondary/60 py-24" data-testid="gcc-models-section">
        <div className="container-site">
          <Reveal>
            <SectionHead eyebrow="Engagement models" title="Five ways to work with us." />
          </Reveal>
          <div className="mt-14 grid gap-px border border-border bg-border sm:grid-cols-2 lg:grid-cols-3">
            {MODELS.map((m, i) => (
              <Reveal key={m.title} delay={i * 0.06} className="bg-card">
                <div className="flex h-full flex-col p-8" data-testid={`gcc-model-${i}`}>
                  <h3 className="text-lg font-bold text-foreground">{m.title}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">{m.body}</p>
                  <p className="mt-4 text-xs font-semibold uppercase tracking-[0.15em] text-accent">{m.best}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" data-testid="gcc-why-section">
        <div className="container-site">
          <Reveal>
            <SectionHead eyebrow="Why firms choose us" title="Built for the Gulf business day." />
          </Reveal>
          <div className="mt-14 grid gap-px border border-border bg-border sm:grid-cols-2 lg:grid-cols-4">
            {WHY.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.08} className="bg-card">
                <div className="flex h-full flex-col p-8" data-testid={`gcc-why-${i}`}>
                  <w.icon size={24} className="text-accent" strokeWidth={1.5} />
                  <h3 className="mt-5 text-lg font-bold text-foreground">{w.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{w.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-secondary/60 py-24" data-testid="gcc-faq-section">
        <div className="container-site max-w-3xl">
          <Reveal>
            <SectionHead eyebrow="FAQs" title="Before you build." />
            <Accordion type="single" collapsible className="mt-10 w-full" data-testid="gcc-faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-base font-medium text-foreground" data-testid={`gcc-faq-trigger-${i}`}>
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-muted-foreground">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <section className="bg-ink py-20" data-testid="gcc-final-cta">
        <div className="container-site flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
          <div>
            <h2 className="max-w-xl text-3xl font-bold tracking-tight text-ink-fg md:text-4xl">Peak season is coming. Add capacity before it does.</h2>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-ink-muted">
              Start with a two-week pilot on live work. If it doesn't meet your review standard, you don't continue.
            </p>
          </div>
          <CalendlyCta label="Book a pilot" testId="gcc-bottom-book-btn" />
        </div>
      </section>
    </div>
  );
}
